import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { useAuth, ROLES } from '../contexts/AuthContext';
import './ShipmentManager.css';

const ShipmentManager = () => {
  const { hasAnyRole } = useAuth();
  const canTogglePublish = hasAnyRole([ROLES.ADMINISTRATOR, ROLES.SUPER_USER]);
  const [shipments, setShipments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);

  const [formData, setFormData] = useState({
    shipment_id: '',
    supplier_id: '',
    order_date: '',
    shipment_date: '',
    received_date: '',
    payment_date: '',
    payment_mode: '',
    payment_particulars: '',
    payment_amt: '',
    order_value: '',
    shipping_charges: '',
    handling_charges: '',
    publish_status: false
  });

  const paymentModes = ['Cash', 'Cheque', 'NEFT', 'Electronic transfer'];

  useEffect(() => {
    fetchShipments();
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/suppliers');
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch suppliers', err);
      setSuppliers([]);
    }
  };

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/shipments');
      setShipments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      let errorMsg = 'Failed to fetch shipments';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else {
          errorMsg = JSON.stringify(err.response.data.detail);
        }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        payment_amt: formData.payment_amt ? parseFloat(formData.payment_amt) : null,
        order_value: parseFloat(formData.order_value),
        shipping_charges: formData.shipping_charges ? parseFloat(formData.shipping_charges) : null,
        handling_charges: formData.handling_charges ? parseFloat(formData.handling_charges) : null,
        shipment_date: formData.shipment_date || null,
        received_date: formData.received_date || null,
        payment_date: formData.payment_date || null,
        payment_mode: formData.payment_mode || null,
        payment_particulars: formData.payment_particulars || null,
        publish_status: Boolean(formData.publish_status)
      };

      if (editingShipment) {
        await axios.put(`/api/shipments/${editingShipment.id}`, payload);
        setSuccess('Shipment updated successfully');
      } else {
        await axios.post('/api/shipments', payload);
        setSuccess('Shipment created successfully');
      }
      resetForm();
      fetchShipments();
    } catch (err) {
      let errorMsg = 'Failed to save shipment';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
    }
  };

  const handleEdit = (shipment) => {
    setEditingShipment(shipment);
    setFormData({
      shipment_id: shipment.shipment_id,
      supplier_id: shipment.supplier_id ? shipment.supplier_id.toString() : '',
      order_date: shipment.order_date,
      shipment_date: shipment.shipment_date || '',
      received_date: shipment.received_date || '',
      payment_date: shipment.payment_date || '',
      payment_mode: shipment.payment_mode || '',
      payment_particulars: shipment.payment_particulars || '',
      payment_amt: shipment.payment_amt ? shipment.payment_amt.toString() : '',
      order_value: shipment.order_value.toString(),
      shipping_charges: shipment.shipping_charges ? shipment.shipping_charges.toString() : '',
      handling_charges: shipment.handling_charges ? shipment.handling_charges.toString() : '',
      publish_status: Boolean(shipment.publish_status)
    });
    setShowForm(true);
  };

  const buildShipmentPayload = (shipment, publishStatusOverride = shipment.publish_status) => ({
    shipment_id: shipment.shipment_id,
    supplier_id: shipment.supplier_id,
    order_date: shipment.order_date,
    shipment_date: shipment.shipment_date,
    received_date: shipment.received_date,
    payment_date: shipment.payment_date,
    payment_mode: shipment.payment_mode,
    payment_particulars: shipment.payment_particulars,
    payment_amt: shipment.payment_amt,
    order_value: shipment.order_value,
    shipping_charges: shipment.shipping_charges,
    handling_charges: shipment.handling_charges,
    publish_status: Boolean(publishStatusOverride)
  });

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const confirmActionClass = /delete|cancel|remove|unpublish/i.test(confirmDialog.confirmText || '')
    ? 'btn btn-danger'
    : 'btn btn-primary';

  const handleTogglePublishStatus = async (shipment) => {
    if (!canTogglePublish) {
      setError('Only administrators can change shipment publish status');
      return;
    }

    const nextStatus = !Boolean(shipment.publish_status);
    const action = nextStatus ? 'publish' : 'unpublish';
    openConfirmDialog(
      nextStatus ? 'Publish Shipment' : 'Unpublish Shipment',
      `Are you sure you want to ${action} shipment ${shipment.shipment_id}?`,
      nextStatus ? 'Publish' : 'Unpublish',
      async () => {
        try {
          const payload = buildShipmentPayload(shipment, nextStatus);
          await axios.put(`/api/shipments/${shipment.id}`, payload);
          setSuccess(`Shipment ${shipment.shipment_id} ${nextStatus ? 'published' : 'unpublished'} successfully`);
          setShipments((prev) => prev.map((item) => (
            item.id === shipment.id ? { ...item, publish_status: nextStatus } : item
          )));
        } catch (err) {
          let errorMsg = `Failed to ${action} shipment`;
          if (err.response?.data?.detail) {
            errorMsg = typeof err.response.data.detail === 'string'
              ? err.response.data.detail
              : JSON.stringify(err.response.data.detail);
          }
          setError(errorMsg);
        }
      }
    );
  };

  const handleDelete = async (id) => {
    openConfirmDialog('Delete Shipment', 'Are you sure you want to delete this shipment?', 'Delete', async () => {
      try {
        await axios.delete(`/api/shipments/${id}`);
        setSuccess('Shipment deleted successfully');
        fetchShipments();
      } catch (err) {
        setError('Failed to delete shipment');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      shipment_id: '',
      supplier_id: '',
      order_date: '',
      shipment_date: '',
      received_date: '',
      payment_date: '',
      payment_mode: '',
      payment_particulars: '',
      payment_amt: '',
      order_value: '',
      shipping_charges: '',
      handling_charges: '',
      publish_status: false
    });
    setEditingShipment(null);
    setShowForm(false);
  };

  return (
    <div className="shipment-manager">
      <div className="header-section">
        <h1>Shipment Summary</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Add Shipment
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{editingShipment ? 'Edit Shipment' : 'Add Shipment'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="shipment-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Shipment ID *</label>
                    <input
                      type="text"
                      name="shipment_id"
                      value={formData.shipment_id}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Supplier</label>
                    <select
                      name="supplier_id"
                      value={formData.supplier_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.supplier_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Order Date *</label>
                    <input
                      type="date"
                      name="order_date"
                      value={formData.order_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Shipment Date</label>
                    <input
                      type="date"
                      name="shipment_date"
                      value={formData.shipment_date}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Received Date</label>
                    <input
                      type="date"
                      name="received_date"
                      value={formData.received_date}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Payment Date</label>
                    <input
                      type="date"
                      name="payment_date"
                      value={formData.payment_date}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select
                      name="payment_mode"
                      value={formData.payment_mode}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Mode</option>
                      {paymentModes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Payment Particulars</label>
                  <input
                    type="text"
                    name="payment_particulars"
                    value={formData.payment_particulars}
                    onChange={handleInputChange}
                    maxLength="100"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Order Value *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="order_value"
                      value={formData.order_value}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      name="payment_amt"
                      value={formData.payment_amt}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Shipping Charges</label>
                    <input
                      type="number"
                      step="0.01"
                      name="shipping_charges"
                      value={formData.shipping_charges}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Handling Charges</label>
                    <input
                      type="number"
                      step="0.01"
                      name="handling_charges"
                      value={formData.handling_charges}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingShipment ? 'Update' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <p>Loading shipments...</p>
        ) : (
          <table className="shipment-table">
            <thead>
              <tr>
                <th>Shipment ID</th>
                <th>Supplier (ID)</th>
                <th>Order Date</th>
                <th>Status</th>
                <th>Publish</th>
                <th>Order Value</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shipments.length > 0 ? (
                shipments.map((shipment) => (
                  <tr key={shipment.id}>
                    <td>{shipment.shipment_id}</td>
                    <td>
                      {shipment.supplier_id ? (
                        <>
                          {suppliers.find(s => s.id === shipment.supplier_id)?.supplier_name || 'Unknown'}
                          <span style={{ fontSize: '0.8rem', color: '#999', marginLeft: '5px' }}>
                            (#{shipment.supplier_id})
                          </span>
                        </>
                      ) : '-'}
                    </td>
                    <td>{shipment.order_date}</td>
                    <td>
                      {shipment.received_date ? (
                        <span className="status-received">Received ({shipment.received_date})</span>
                      ) : (
                        <span className="status-pending">In Transit / Pending</span>
                      )}
                    </td>
                    <td>
                      <span className={shipment.publish_status ? 'status-publish-on' : 'status-publish-off'}>
                        {shipment.publish_status ? 'Published' : 'Unpublished'}
                      </span>
                    </td>
                    <td>{shipment.order_value}</td>
                    <td>{shipment.payment_mode ? `${shipment.payment_mode} (${shipment.payment_amt || 0})` : 'Pending'}</td>
                    <td>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(shipment)}>Edit</button>
                      {canTogglePublish && (
                        <button
                          className={`btn btn-sm ${shipment.publish_status ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleTogglePublishStatus(shipment)}
                        >
                          {shipment.publish_status ? 'Unpublish' : 'Publish'}
                        </button>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(shipment.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">No shipments found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className={confirmActionClass} onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentManager;
